import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    EuiButton,
    EuiFlexGroup,
    EuiFlexItem,
    EuiModal,
    EuiModalBody,
    EuiModalHeader,
    EuiModalHeaderTitle,
    EuiRadioGroup,
    EuiSpacer,
    EuiTitle,
} from '@elastic/eui';
import { isEmpty } from 'lodash';
import { userHasPermission } from '../../../helper/auth';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { baseApiUrl } from '../../../envConfig';
import { useDropzone } from 'react-dropzone';
import Resizer from 'react-image-file-resizer';
import UploadImageErrorMsg from './UploadImageErrorMsg';
import { cropAnimatedGif } from '../utils/cropAnimatedGif';

const immersiveAspectRatioOptions = [
    { id: '0.25', label: 'Small (4:1)' },
    { id: '0.5', label: 'Large (2:1)' },
    { id: '0.83', label: 'Infinity (6:5)' },
];

// A GIF with a different shape is flagged before it reaches the cropper (cropAnimatedGif keeps the animation)
const GIF_RATIO_TOLERANCE = 0.01; // 1% relative difference

const formatRatio = (ratio) => {
    if (ratio >= 1) return `${+ratio.toFixed(2)}:1`;
    return `1:${+(1 / ratio).toFixed(2)}`;
};

const getGifRatioError = (width, height, aspectRatio) => {
    if (!aspectRatio || !width || !height) return null;
    const fileRatio = width / height;
    if (Math.abs(fileRatio - aspectRatio) / aspectRatio <= GIF_RATIO_TOLERANCE) return null;
    const exampleWidth = 400;
    return (
        `This GIF doesn't match the banner shape. ` +
        `Required aspect ratio: ${formatRatio(aspectRatio)} (e.g. ${exampleWidth} × ${Math.round(
            exampleWidth / aspectRatio
        )} px). ` +
        `Your GIF: ${width} × ${height} px (${formatRatio(fileRatio)}). ` +
        `Continue to crop it to ${formatRatio(aspectRatio)} (the animation is kept), or close and pick another file.`
    );
};

const UploadImageDropZone = ({
    handleUploadImageModalToggle,
    imageType = 'categories',
    fetchImages,
    aspectRatio,
    isPreviewOn,
    isImmersive,
    aspectRatioValue,
    onAspectRatioChange,
}) => {
    const [image, setImage] = useState(null);
    const restaurantID = useSelector((state) => state.auth.selectedRestaurantId);
    const [cropper, setCropper] = useState();
    const [imageValue, setImageValue] = useState();
    const [buttonDisable, setButtonDisable] = useState(false);
    const permissions = useSelector((state) => state.permission.permission);
    const history = useHistory();
    const token = localStorage.getItem('authToken');
    const [errorMessage, setErrorMessage] = useState('');
    const [isErrorModalVisible, setIsErrorModalVisible] = useState(false);
    const [errorPreview, setErrorPreview] = useState(null);
    let cropperRef = useRef(null);

    // Update cropper aspect ratio live when radio changes
    useEffect(() => {
        if (cropper && isImmersive && aspectRatio) {
            cropper.setAspectRatio(aspectRatio);
        }
    }, [cropper, isImmersive, aspectRatio]);

    const handleFile = useCallback(
        (file) => {
            if (!file) return;
            if (file.type === 'image/gif') {
                const url = URL.createObjectURL(file);
                const img = new Image();
                img.onload = () => {
                    const error = getGifRatioError(img.width, img.height, aspectRatio);
                    if (error) {
                        // keep the URL alive for the error preview; revoked in closeErrorModal
                        setErrorPreview({ url, file, width: img.width, height: img.height, aspectRatio });
                        setErrorMessage(error);
                        setIsErrorModalVisible(true);
                        return;
                    }
                    setImage(url);
                    setImageValue({ file, filename: file.name, fileType: file.type });
                };
                img.onerror = () => {
                    URL.revokeObjectURL(url);
                    setErrorMessage('Unable to read this GIF. Please try another file.');
                    setIsErrorModalVisible(true);
                };
                img.src = url;
            } else {
                const reader = new FileReader();
                reader.onload = () => {
                    setImage(reader.result);
                    setImageValue({ file, filename: file.name, fileType: file.type });
                };
                reader.readAsDataURL(file);
            }
        },
        [aspectRatio]
    );

    const closeErrorModal = useCallback(() => {
        setIsErrorModalVisible(false);
        setErrorPreview((prev) => {
            if (prev?.url) URL.revokeObjectURL(prev.url);
            return null;
        });
    }, []);

    // Accept the mismatched GIF anyway; the object URL is reused for the upload preview, so don't revoke it
    const continueWithGif = useCallback(() => {
        if (!errorPreview?.file) return;
        const { url, file } = errorPreview;
        setImage(url);
        setImageValue({ file, filename: file.name, fileType: file.type });
        setErrorPreview(null);
        setIsErrorModalVisible(false);
    }, [errorPreview]);

    const onDrop = useCallback((acceptedFiles) => handleFile(acceptedFiles[0]), [handleFile]);

    const onChange = useCallback(
        (e) => {
            e.preventDefault();
            handleFile(e.target.files[0]);
        },
        [handleFile]
    );

    const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
        onDrop: onDrop,
        maxFiles: 1,
    });

    const style = useMemo(
        () => ({
            ...baseStyle,
            ...(isFocused ? focusedStyle : {}),
            ...(isDragAccept ? acceptStyle : {}),
            ...(isDragReject ? rejectStyle : {}),
        }),
        [isFocused, isDragAccept, isDragReject]
    );

    const handleDeletion = useCallback(() => {
        if (typeof image === 'string' && image.startsWith('blob:')) {
            URL.revokeObjectURL(image);
        }
        setImageValue(null);
        setImage(null);
        setCropper(null);
    }, [image]);

    const dataURLtoFile = useCallback((dataurl, filename) => {
        var arr = dataurl.split(','),
            mime = arr[0].match(/:(.*?);/)[1],
            bstr = atob(arr[1]),
            n = bstr.length,
            u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], filename, { type: mime });
    }, []);

    const resizeFile = (data) =>
        new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                const maxWidth = 2048;
                const maxHeight = 1024;

                if (img.width > maxWidth || img.height > maxHeight) {
                    Resizer.imageFileResizer(
                        data,
                        maxWidth,
                        maxHeight,
                        'PNG',
                        100,
                        0,
                        (uri) => {
                            resolve(dataURLtoFile(uri, imageValue?.filename));
                        },
                        'base64',
                        (err) => {
                            reject(err);
                        }
                    );
                } else {
                    resolve(data);
                }
            };
            img.onerror = reject;
            img.src = URL.createObjectURL(data);
        });

    const submitFormData = useCallback(
        (formData) => {
            fetch(`${baseApiUrl}/restaurants/${restaurantID}/uploads`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: formData,
            })
                .then((response) => {
                    setButtonDisable(false);
                    handleUploadImageModalToggle();
                    fetchImages(imageType);
                })
                .catch((error) => {
                    setButtonDisable(false);
                    window.alert(error);
                });
        },
        [handleUploadImageModalToggle, fetchImages, imageType, restaurantID, token]
    );

    const handleUploadAction = useCallback(async () => {
        setButtonDisable(true);
        let formData = new FormData();

        if (isImmersive) {
            formData.append('ratio', aspectRatioValue || '0.25');
        }

        if (imageValue?.fileType === 'image/gif') {
            try {
                const croppedGif = await cropAnimatedGif(imageValue.file, cropper.getData(true));
                formData.append('image', croppedGif);
                formData.append('image_type', imageType);
                formData.append('original_file_name', imageValue?.filename);
                submitFormData(formData);
            } catch (error) {
                console.error('Error cropping GIF:', error);
                setButtonDisable(false);
                setErrorMessage('Error cropping this GIF. Please try another file.');
                setIsErrorModalVisible(true);
            }
        } else {
            try {
                const data = cropper.getCroppedCanvas().toDataURL();
                console.log('Cropped image data URL:', data);

                const resizedFile = await resizeFile(dataURLtoFile(data, imageValue?.filename));
                console.log('Resized file:', resizedFile);

                formData.append('image', resizedFile);
                formData.append('image_type', imageType);
                formData.append('original_file_name', imageValue?.filename);

                submitFormData(formData);
            } catch (error) {
                console.error('Error cropping or resizing image:', error);
                setButtonDisable(false);
                setErrorMessage('Error cropping or resizing image.');
                setIsErrorModalVisible(true);
            }
        }
    }, [cropper, imageValue, imageType, resizeFile, dataURLtoFile, submitFormData, isImmersive, aspectRatioValue]);

    return (
        <>
            <EuiModal onClose={handleUploadImageModalToggle}>
                <EuiModalHeader>
                    <EuiModalHeaderTitle>
                        <h1>Upload Image</h1>
                    </EuiModalHeaderTitle>
                </EuiModalHeader>
                <EuiModalBody style={{ minWidth: '780px', overflowY: 'scroll' }} className='scrollbar'>
                    <div>
                        {isImmersive && !isEmpty(imageValue) && (
                            <>
                                <EuiFlexGroup alignItems='center' gutterSize='m'>
                                    <EuiFlexItem grow={false}>
                                        <strong>Select aspect ratio:</strong>
                                    </EuiFlexItem>
                                    {immersiveAspectRatioOptions.map((option) => (
                                        <EuiFlexItem grow={false} key={option.id}>
                                            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '4px' }}>
                                                <input
                                                    type='radio'
                                                    name='immersive_aspect_ratio'
                                                    value={option.id}
                                                    checked={(aspectRatioValue || '0.25') === option.id}
                                                    onChange={() => onAspectRatioChange(option.id)}
                                                />
                                                {option.label}
                                            </label>
                                        </EuiFlexItem>
                                    ))}
                                </EuiFlexGroup>
                                <EuiSpacer size='m' />
                            </>
                        )}
                        <div style={{ width: '100%' }}>
                            {isEmpty(imageValue) ? (
                                <section style={{ maxWidth: '97%' }}>
                                    <div {...getRootProps({ style })}>
                                        <input
                                            {...getInputProps()}
                                            onChange={onChange}
                                            accept='image/gif , image/png , image/jpeg , image/jpg'
                                        />
                                        <p>Drag 'n' drop some files here, or click to select files</p>
                                        <p>Accepted file types: .jpeg, .jpg, .png , .gif Max file size: 5MB</p>
                                    </div>
                                </section>
                            ) : null}
                            <EuiSpacer />
                            {!isEmpty(imageValue) ? (
                                <Cropper
                                    style={{ maxHeight: 400, width: '100%' }}
                                    // zoomTo={0.5}
                                    initialAspectRatio={aspectRatio}
                                    aspectRatio={aspectRatio}
                                    preview='.img-preview'
                                    src={image}
                                    viewMode={1}
                                    minCropBoxHeight={10}
                                    minCropBoxWidth={10}
                                    background={false}
                                    responsive={true}
                                    autoCropArea={1}
                                    checkOrientation={false}
                                    onInitialized={(instance) => {
                                        setCropper(instance);
                                    }}
                                    maxLength={5242880}
                                    guides={true}
                                    ref={cropperRef}
                                />
                            ) : null}
                        </div>
                        {!isEmpty(imageValue) && isPreviewOn ? (
                            <>
                                <EuiFlexGroup gutterSize='none' alignItems='center' justifyContent='center'>
                                    <EuiFlexItem>
                                        <div className='box' style={{ width: '50%', float: 'right' }}>
                                            <EuiTitle size='s'>
                                                <h5>Preview</h5>
                                            </EuiTitle>
                                            <div
                                                className='img-preview'
                                                style={{ width: '100%', float: 'left', height: '300px' }}
                                            />
                                        </div>
                                    </EuiFlexItem>
                                </EuiFlexGroup>
                            </>
                        ) : null}
                    </div>
                    {!isEmpty(imageValue) ? (
                        <>
                            <EuiFlexGroup style={{ margin: 10, width: '100%' }} alignItems='center'>
                                <EuiFlexItem
                                    style={{
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        justifyContent: 'space-around',
                                    }}
                                >
                                    <>
                                        <EuiFlexItem grow={false} style={{ marginRight: '10px' }}>
                                            <EuiButton
                                                isLoading={
                                                    buttonDisable ||
                                                    !userHasPermission(permissions, history.location.pathname, 'write')
                                                }
                                                fill
                                                onClick={handleUploadAction}
                                            >
                                                Upload
                                            </EuiButton>
                                        </EuiFlexItem>
                                        <EuiFlexItem grow={false}>
                                            <EuiButton
                                                fill
                                                color='danger'
                                                iconType='trash'
                                                disabled={buttonDisable}
                                                onClick={handleDeletion}
                                            >
                                                Remove
                                            </EuiButton>
                                        </EuiFlexItem>
                                    </>
                                </EuiFlexItem>
                            </EuiFlexGroup>
                        </>
                    ) : null}
                </EuiModalBody>
            </EuiModal>
            {isErrorModalVisible && (
                <UploadImageErrorMsg
                    message={errorMessage}
                    preview={errorPreview}
                    onClose={closeErrorModal}
                    onContinue={errorPreview?.file ? continueWithGif : undefined}
                />
            )}
        </>
    );
};

export default React.memo(UploadImageDropZone);

const baseStyle = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    borderWidth: 2,
    borderRadius: 2,
    borderColor: '#eeeeee',
    borderStyle: 'dashed',
    backgroundColor: '#fafafa',
    color: '#bdbdbd',
    outline: 'none',
    transition: 'border .24s ease-in-out',
};

const focusedStyle = {
    borderColor: '#2196f3',
};

const acceptStyle = {
    borderColor: '#00e676',
};

const rejectStyle = {
    borderColor: '#ff1744',
};
